import { useTranslation } from 'react-i18next';

import {
  FormatCurrencyForDisplay,
  SkeletonBase,
  SkeletonLoader,
} from '@unified-checkout/design-system';

import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';
import { useLangStore } from '@app/dashboard/store/lang';

import type { ISettlementBalanceFees } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/instant-settlement';

import styles from './destination.module.scss';

const Destination = ({
  isLoading,
  settlementBalanceFees,
}: {
  isLoading: boolean;
  settlementBalanceFees: ISettlementBalanceFees;
}) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const formattedAmount = (amount: number) => {
    const formattedAmount = formatAmountWithCurrency({
      amount,
      currency: settlementBalanceFees?.bank_account_details?.currency,
      lang,
    });

    return FormatCurrencyForDisplay(formattedAmount);
  };

  const fees = settlementBalanceFees?.settlement_fee || 0;
  const amountPound = settlementBalanceFees?.amount_pound || 0;
  const netAmount = settlementBalanceFees?.settlement_balance;
  const bankAccountDetails = settlementBalanceFees?.bank_account_details;
  const feePercentage = fees && (fees / amountPound) * 100;

  return (
    <div className={styles['settlement-destination']}>
      <div className={styles['settlement-destination__bank']}>
        {/* <div
          className={styles['settlement-destination__bank__logo']}
          data-bank-logo-loading={isLoading}
        >
          // TODO: will uncomment after handled from BE side, Value icon just a dummy icon
          {isLoading ? <SkeletonBase width="100%" height="2.357rem" /> : Valu}
        </div> */}
        <p className={styles['settlement-destination__bank__label']}>
          {t(
            'innerLayout.balanceTransfers.balance.instantSettlement.settlementAccount'
          )}
        </p>
        {isLoading ? (
          <SkeletonLoader
            noOfRows={2}
            width="100%"
            height="2.25rem"
            type="transparent_box"
          />
        ) : (
          <div className={styles['settlement-destination__bank__details']}>
            <p
              className={styles['settlement-destination__bank__details__name']}
            >
              {bankAccountDetails?.account_name}
            </p>

            <p
              className={
                styles['settlement-destination__bank__details__account']
              }
            >
              {bankAccountDetails?.bank_name}{' '}
              {bankAccountDetails?.account_number}
            </p>
          </div>
        )}
      </div>
      <div className={styles['settlement-destination__amount']}>
        <p className={styles['settlement-destination__amount__label']}>
          {t(
            'innerLayout.balanceTransfers.balance.instantSettlement.youllReceive'
          )}
        </p>
        {isLoading ? (
          <SkeletonLoader
            noOfRows={2}
            width="100%"
            height="4rem"
            type="transparent_box"
          />
        ) : (
          <div className={styles['settlement-destination__amount__value']}>
            <p className={styles['settlement-destination__amount__value__net']}>
              {formattedAmount(netAmount || 0)}
            </p>
            <p
              className={styles['settlement-destination__amount__value__fees']}
            >
              {t('innerLayout.balanceTransfers.balance.instantSettlement.fee', {
                percentage: feePercentage.toFixed(2),
              })}{' '}
              - {formattedAmount(fees || 0)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Destination;
